import { useId, useState } from 'react';
import { VisuallyHidden } from '../atoms/VisuallyHidden';
import styles from './LineChart.module.css';

export interface LineChartSeries {
  key: string;
  label: string;
  /** One value per category, in category order. */
  values: number[];
}

export interface LineChartProps {
  /** Accessible name; also the caption of the table view. */
  label: string;
  /** Labels along the x-axis, e.g. years. */
  categories: string[];
  /** Long labels for tooltip and table (defaults to `categories`). */
  categoryTitles?: string[];
  /** At most two series (colour slots 1 and 2), sharing one axis. */
  series: LineChartSeries[];
  format: (value: number) => string;
  /** Header of the first table column. */
  categoryHeader: string;
}

const SLOTS = ['var(--chart-1)', 'var(--chart-2)'];

/** A rounded upper bound for the axis, e.g. 1234 → 1500. */
function niceMax(max: number): number {
  if (max <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(max));
  const steps = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  return (steps.find((s) => s * exp >= max) ?? 10) * exp;
}

/**
 * Values over time (e.g. yield per year) as lines on one axis. Hover or
 * focus a point for its values with a crosshair; screen readers get a table.
 */
export function LineChart({
  label,
  categories,
  categoryTitles,
  series,
  format,
  categoryHeader,
}: LineChartProps) {
  const id = useId();
  const [active, setActive] = useState<number | null>(null);
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)));
  const titles = categoryTitles ?? categories;
  const ticks = [max, max / 2, 0];
  const n = categories.length;
  // Points sit in the middle of their column.
  const x = (i: number) => ((i + 0.5) / Math.max(n, 1)) * 100;
  const y = (v: number) => 100 - (Math.max(0, v) / max) * 100;

  return (
    <figure className={styles.figure} aria-labelledby={`${id}-label`}>
      <VisuallyHidden>
        <span id={`${id}-label`}>{label}</span>
      </VisuallyHidden>
      {series.length > 1 && (
        <ul className={styles.legend} aria-hidden="true">
          {series.map((s, i) => (
            <li key={s.key}>
              <span className={styles.swatch} style={{ background: SLOTS[i] }} />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div className={styles.plot} aria-hidden="true">
        <div className={styles.axis}>
          {ticks.map((t) => (
            <span key={t}>{format(t)}</span>
          ))}
        </div>
        <div className={styles.area}>
          <div className={styles.canvas}>
            {ticks.map((t) => (
              <span key={t} className={styles.grid} style={{ bottom: `${(t / max) * 100}%` }} />
            ))}
            <svg className={styles.svg} viewBox="0 0 100 100" preserveAspectRatio="none">
              {series.map((s, si) => (
                <polyline
                  key={s.key}
                  fill="none"
                  stroke={SLOTS[si]}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
                />
              ))}
            </svg>
            {active !== null && (
              <span className={styles.crosshair} style={{ left: `${x(active)}%` }} />
            )}
            {series.map((s, si) =>
              s.values.map((v, i) => (
                <span
                  key={`${s.key}-${i}`}
                  className={styles.marker}
                  data-active={active === i || undefined}
                  style={{ left: `${x(i)}%`, top: `${y(v)}%`, background: SLOTS[si] }}
                />
              )),
            )}
          </div>
          <div className={styles.columns}>
            {categories.map((c, ci) => (
              <div
                key={`${c}-${ci}`}
                className={styles.column}
                onMouseEnter={() => setActive(ci)}
                onMouseLeave={() => setActive(null)}
              >
                <span className={styles.tick}>{c}</span>
                {active === ci && (
                  <div className={styles.tooltip} role="presentation">
                    <strong>{titles[ci]}</strong>
                    {series.map((s, si) => (
                      <span key={s.key} className={styles.tipRow}>
                        <span className={styles.swatch} style={{ background: SLOTS[si] }} />
                        {s.label}
                        <span className={styles.tipValue}>{format(s.values[ci] ?? 0)}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <VisuallyHidden>
        <table>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th scope="col">{categoryHeader}</th>
              {series.map((s) => (
                <th key={s.key} scope="col">
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {titles.map((c, ci) => (
              <tr key={`${c}-${ci}`}>
                <th scope="row">{c}</th>
                {series.map((s) => (
                  <td key={s.key}>{format(s.values[ci] ?? 0)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </VisuallyHidden>
    </figure>
  );
}
